import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NgForm, Validators, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

// models
import { Label } from '../../../models/label';
import { PanelHeaderTitle } from '../../../../core/services/side-panel.service';

// services
import { SidePanelService } from '../../../../core/services/side-panel.service';
import { GlobalSettings } from '../../../../core/models/globalSettings';
import { POSTextMaterial } from '../../../../planogram/models/POSMaterials/POSTextMaterial';
import { UnitConverter } from '../../../../core/utils/unitConverter';

@Component({
    selector: 'app-label-configuration',
    templateUrl: './label-configuration.component.html',
    styleUrls: ['./label-configuration.component.scss'],
    standalone: false
})
export class LabelConfigurationComponent implements OnInit, OnDestroy {

	protected readonly MORE_SYMBOL_VALIDATION: number = GlobalSettings.MORE_SYMBOL_VALIDATION;

	public label: Label;
	public formGroup: UntypedFormGroup = null;
	public isWidthConfig: boolean = false;
	public subscriptions: Array<Subscription> = [];

	@Input('label') set updateLabel(value: Label | POSTextMaterial) {
		if (value instanceof POSTextMaterial) {
			this.isWidthConfig = true;
			value = new Label(value.x, value.y, value.name, value.angle, value.fontSize, value.color, value.width);
		}
		this.init(value);
	};

	@Output() apply: EventEmitter<Label> = new EventEmitter<Label>();

	@ViewChild('form') formElement: NgForm;

	constructor(private panelService: SidePanelService) {}

	ngOnInit(): void {
		this.panelService.title = PanelHeaderTitle.TextSettings;

		this.subscriptions.push(
			this.panelService.applySubject.subscribe(_ => {
				this.formElement.onSubmit(this.formGroup.value);
			}),
			this.panelService.closeSubject.subscribe(_ => {
				this.init(this.label);
			})
		);
	}

	applyPanel(): void {
		this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;

		if (!this.formGroup.get('angle').value) this.formGroup.get('angle').setValue(0);
		if (!this.formGroup.get('text').value) this.formGroup.get('text').setValue('Text');
		if (this.formGroup.get('fontSize').value === null) this.formGroup.get('fontSize').setValue(16);
		if (this.formElement.invalid) return;

		const value = this.formGroup.value;

		this.label.name = value.text;
		this.label.fontSize = value.fontSize;
		this.label.color = value.textColor;
		this.label.angle = value.angle;
		if (this.isWidthConfig) this.label.width = UnitConverter.getSaveConvertValue(value.width);

		this.apply.emit(this.label);
		this.panelService.disabledApply = true;
	}

	init(label: Label): void {
		this.label = label.copy();
		this.setFormGroup();
	}

	public numberInputValidate(event: KeyboardEvent, validDot: boolean = false): boolean {
		const blockedSymbols: Array<string> = ['e', 'E', '-', '+'];
		if (!validDot) blockedSymbols.push('.', ',');
		return !blockedSymbols.includes(event.key);
	}

	setFormGroup(): void {
		this.formGroup = new UntypedFormGroup({
			text: new UntypedFormControl(
				this.label.name,
				[Validators.required, Validators.maxLength(this.MORE_SYMBOL_VALIDATION)]
			),
			fontSize: new UntypedFormControl(this.label.fontSize, [
				Validators.required,
				Validators.min(1),
				Validators.pattern('\\d+')
			]),
			textColor: new UntypedFormControl(this.label.color),
			angle: new UntypedFormControl(this.label.angle || 0, [
				Validators.pattern(/(?!-)\d+/),
				Validators.min(-360),
				Validators.max(360)
			])
		});
		if (this.isWidthConfig) {
			this.formGroup.addControl('width', new FormControl<number>(UnitConverter.getConvertValue(this.label.width)));
		}
		this.formGroup.valueChanges.subscribe(() => {
			this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
		});
		if (this.formElement) this.formElement.onReset();
		this.panelService.disabledApply = true;
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
